import React from 'react';
import { Typography, Paper, Box, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Card, Chip } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimelineIcon from '@mui/icons-material/Timeline';
import DomainIcon from '@mui/icons-material/Domain';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import { formatCurrency } from '../../utils/formatters';
import InvestidorCorrigidoItem from '../InvestidorCorrigidoItem';

const MetricCard = ({ title, value, icon, color = 'primary.main', action }: any) => (
    <Card elevation={2} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 1, color, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                {action}
            </Box>
        </Box>
        <Box sx={{ 
            backgroundColor: `${color}15`, 
            borderRadius: '50%', 
            p: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            color: color,
            ml: 1
        }}>
            {icon}
        </Box>
    </Card>
);

interface ObraBalancoTabProps {
    balanco: any;
    obra: any;
    valoresCorrigidos: any;
    onOpenValoresCorrigidos: () => void;
}

export const ObraBalancoTab: React.FC<ObraBalancoTabProps> = ({ balanco, obra, valoresCorrigidos, onOpenValoresCorrigidos }) => {
    if (!balanco) return null;
    const { kpis, detalhes_projeto, analise_investimento } = balanco;
    const unidadesList = Array.isArray(balanco.unidades) && balanco.unidades.length > 0
        ? balanco.unidades
        : Array.isArray(obra?.unidades)
        ? obra.unidades
        : [];

    const receita = kpis.receita_bruta_projetada || 0;
    const custo = kpis.custo_total_realizado || 0;
    const roi = custo > 0 ? ((receita - custo) / custo) * 100 : 0;

    const areaConstruidaTotal =
        Number(detalhes_projeto.area_construida_total) ||
        unidadesList.reduce((acc: number, u: any) => acc + (Number(u.area_construida) || 0), 0);
    const areaNaoConstruidaTotal =
        Number(detalhes_projeto.area_nao_construida_total) ||
        unidadesList.reduce((acc: number, u: any) => acc + (Number(u.area_nao_construida) || 0), 0);
    const areaPrivativaTotal =
        Number(detalhes_projeto.area_privativa_total) ||
        unidadesList.reduce(
            (acc: number, u: any) =>
                acc +
                (Number(
                    u.area_privativa_total ??
                        u.area ??
                        ((Number(u.area_construida) || 0) + (Number(u.area_nao_construida) || 0) || 0)
                ) || 0),
            0
        );
    const totalVagas =
        Number(detalhes_projeto.total_vagas) ||
        unidadesList.reduce((acc: number, u: any) => acc + (Number(u.vagas) || 0), 0);
    const custoPorM2Construido = areaConstruidaTotal > 0 ? custo / areaConstruidaTotal : 0;
    const valorM2Privativo = areaPrivativaTotal > 0 ? receita / areaPrivativaTotal : 0;

    return (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', mx: -1.5 }}>
            <Box sx={{ p: 1.5, width: '100%' }}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>Painel Financeiro Principal</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2 }}>
                        <MetricCard 
                            title="Balanço Atual" 
                            value={formatCurrency(kpis.balanco_atual || 0)} 
                            icon={<AccountBalanceIcon />} 
                            color={kpis.balanco_atual >= 0 ? "primary.main" : "error.main"}
                        />
                        <MetricCard 
                            title="Margem de Lucro" 
                            value={`${(kpis.margem_lucro || 0).toFixed(2)}%`} 
                            icon={<TrendingUpIcon />} 
                            color="success.main"
                        />
                        <MetricCard 
                            title="ROI (Retorno Sobre Investimento)" 
                            value={`${roi.toFixed(2)}%`} 
                            icon={<TimelineIcon />} 
                            color="success.main"
                        />
                        <MetricCard 
                            title="Receita Projetada (VGV)" 
                            value={formatCurrency(receita)} 
                            icon={<AttachMoneyIcon />} 
                            color="primary.main"
                        />
                        <MetricCard 
                            title="Custo Realizado" 
                            value={formatCurrency(kpis.custo_total_realizado || 0)} 
                            icon={<TrendingDownIcon />} 
                            color="error.main"
                        />
                        {valoresCorrigidos && (
                            <MetricCard 
                                title="Custo Corrigido" 
                                value={formatCurrency(valoresCorrigidos.valorCorrigido)} 
                                icon={<TrendingDownIcon />} 
                                color="warning.main"
                                action={<Button size="small" onClick={onOpenValoresCorrigidos} sx={{ mt: { xs: 1, sm: 0 } }}>Detalhes</Button>}
                            />
                        )}
                        <MetricCard 
                            title="Progresso Orçamento" 
                            value={`${(kpis.progresso_orcamento || 0).toFixed(2)}%`} 
                            icon={<DataUsageIcon />} 
                            color={(kpis.progresso_orcamento || 0) > 100 ? "error.main" : "primary.main"}
                        />
                    </Box>
                </Paper>
            </Box>

            <Box sx={{ p: 1.5, width: '100%' }}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>Detalhes do Projeto, Unidades e Investimento</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
                        <MetricCard 
                            title={unidadesList.length > 0 ? "Ticket Médio por Unidade" : "Valor por Unidade"} 
                            value={formatCurrency(detalhes_projeto.valor_venda_unidade || 0)} 
                            icon={<DomainIcon />} 
                        />
                        <MetricCard 
                            title="Qtd. de Unidades" 
                            value={detalhes_projeto.quantidade_unidades || 0} 
                            icon={<FormatListNumberedIcon />} 
                            color="info.main"
                        />
                        <MetricCard 
                            title="Custo Médio por Unidade" 
                            value={formatCurrency(kpis.custo_por_unidade || 0)} 
                            icon={<TrendingDownIcon />} 
                            color="error.main"
                        />
                        {valoresCorrigidos && (
                            <MetricCard 
                                title="Custo p/ Unid. Corrigido" 
                                value={formatCurrency(
                                    detalhes_projeto.quantidade_unidades > 0
                                        ? valoresCorrigidos.valorCorrigido / detalhes_projeto.quantidade_unidades
                                        : 0
                                )} 
                                icon={<TrendingDownIcon />} 
                                color="warning.main"
                            />
                        )}
                        {unidadesList.length > 0 && (
                            <>
                                <MetricCard 
                                    title={`VGV Vendido (${detalhes_projeto.qtd_vendidas || 0} unid.)`} 
                                    value={formatCurrency(detalhes_projeto.vgv_vendido || 0)} 
                                    icon={<CheckCircleOutlineIcon />} 
                                    color="success.main"
                                />
                                <MetricCard 
                                    title={`VGV Disponível (${detalhes_projeto.qtd_disponiveis || 0} unid.)`} 
                                    value={formatCurrency(detalhes_projeto.vgv_disponivel || 0)} 
                                    icon={<StorefrontIcon />} 
                                    color="primary.main"
                                />
                                {areaConstruidaTotal > 0 && (
                                    <MetricCard 
                                        title="Área Construída Total" 
                                        value={`${areaConstruidaTotal.toLocaleString('pt-BR')} m²`} 
                                        icon={<SquareFootIcon />} 
                                        color="info.main"
                                    />
                                )}
                                {areaPrivativaTotal > 0 && (
                                    <MetricCard 
                                        title="Área Privativa Total" 
                                        value={`${areaPrivativaTotal.toLocaleString('pt-BR')} m²`} 
                                        icon={<SquareFootIcon />} 
                                        color="primary.main"
                                    />
                                )}
                                {custoPorM2Construido > 0 && (
                                    <MetricCard 
                                        title="Custo / m² Construído" 
                                        value={formatCurrency(custoPorM2Construido)} 
                                        icon={<TrendingDownIcon />} 
                                        color="error.main"
                                    />
                                )}
                                {valorM2Privativo > 0 && (
                                    <MetricCard 
                                        title="Valor Médio / m² Privativo" 
                                        value={formatCurrency(valorM2Privativo)} 
                                        icon={<AttachMoneyIcon />} 
                                        color="success.main"
                                    />
                                )}
                                {totalVagas > 0 && (
                                    <MetricCard 
                                        title="Total de Vagas" 
                                        value={totalVagas} 
                                        icon={<DirectionsCarOutlinedIcon />} 
                                        color="info.main"
                                    />
                                )}
                            </>
                        )}
                    </Box>

                    {unidadesList.length > 0 && (
                        <Box sx={{ width: '100%', mt: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Quadro Comercial de Unidades (Áreas, Vagas, Valores Individuais e Rateio de Custos)
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>Unidade</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Área Constr.</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Área Não Constr.</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Área Privativa Total</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Vagas</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Valor de Venda</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>% do VGV</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Custo Proporcional</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Resultado Estimado</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {unidadesList.map((u: any) => {
                                            const valorUnidade = Number(u.valor) || 0;
                                            const fracaoVgv = receita > 0 ? valorUnidade / receita : 0;
                                            const custoProporcional = custo * fracaoVgv;
                                            const resultadoUnidade = valorUnidade - custoProporcional;
                                            const areaConstr = u.area_construida != null ? Number(u.area_construida) : null;
                                            const areaNaoConstr = u.area_nao_construida != null ? Number(u.area_nao_construida) : null;
                                            const areaPriv =
                                                u.area_privativa_total != null
                                                    ? Number(u.area_privativa_total)
                                                    : u.area != null
                                                    ? Number(u.area)
                                                    : areaConstr != null || areaNaoConstr != null
                                                    ? (areaConstr || 0) + (areaNaoConstr || 0)
                                                    : null;

                                            return (
                                                <TableRow key={u.id} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{u.identificacao}</TableCell>
                                                    <TableCell align="right">
                                                        {areaConstr != null ? `${areaConstr.toLocaleString('pt-BR')} m²` : '-'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {areaNaoConstr != null ? `${areaNaoConstr.toLocaleString('pt-BR')} m²` : '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        {areaPriv != null ? `${areaPriv.toLocaleString('pt-BR')} m²` : '-'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {u.vagas != null ? u.vagas : '-'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={u.status || 'Disponível'}
                                                            size="small"
                                                            color={
                                                                u.status === 'Disponível'
                                                                    ? 'success'
                                                                    : u.status === 'Vendida'
                                                                    ? 'primary'
                                                                    : 'warning'
                                                            }
                                                            variant="outlined"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                        {formatCurrency(valorUnidade)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(fracaoVgv * 100).toFixed(1).replace('.', ',')}%
                                                    </TableCell>
                                                    <TableCell align="right" color="text.secondary">
                                                        {formatCurrency(custoProporcional)}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: resultadoUnidade >= 0 ? 'success.main' : 'error.main',
                                                        }}
                                                    >
                                                        {formatCurrency(resultadoUnidade)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                Total ({unidadesList.length} unid.)
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {areaConstruidaTotal > 0 ? `${areaConstruidaTotal.toLocaleString('pt-BR')} m²` : '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {areaNaoConstruidaTotal > 0 ? `${areaNaoConstruidaTotal.toLocaleString('pt-BR')} m²` : '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {areaPrivativaTotal > 0 ? `${areaPrivativaTotal.toLocaleString('pt-BR')} m²` : '-'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                                                {totalVagas > 0 ? totalVagas : '-'}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {formatCurrency(receita)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                100,0%
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {formatCurrency(custo)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: receita - custo >= 0 ? 'success.main' : 'error.main',
                                                }}
                                            >
                                                {formatCurrency(receita - custo)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    <Box sx={{ width: '100%', mt: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Investidores e Responsáveis
                        </Typography>
                    </Box>
                    {analise_investimento.distribuicao && analise_investimento.distribuicao.length > 0 ? (
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>Investidor</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Aporte Líquido</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Valor Investido</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Saldo</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Valor Corrigido</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Juros Total</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Juros Mês</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Detalhes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analise_investimento.distribuicao.map((inv: any) => (
                                            <InvestidorCorrigidoItem key={inv.id} investidor={inv} obraId={obra.id} dataTerminoPrevista={obra.data_limite_correcao || obra.data_termino_prevista} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', p: 1 }}><Typography color="text.secondary">Nenhum investidor ou responsável vinculado a esta obra.</Typography></Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};
